import { billingApi } from '@/api';
import { posApi } from '@/api/pos.api';
import type { PaymentRequest } from '@/types/billing';
import type { PaymentMethod } from '@/types/enums';
import { loadRazorpayScript, openRazorpayCheckout } from '@/utils/razorpay';
import { collectStripeOnlinePayment, collectStripeTerminalPayment } from '@/utils/stripeTerminal';

const GATEWAY_PROVIDERS = ['RAZORPAY', 'STRIPE', 'PAYTM', 'CASHFREE', 'PHONEPE', 'PAYU'] as const;

export type GatewayProvider = (typeof GATEWAY_PROVIDERS)[number];

export function isGatewayPaymentMethod(method: string): boolean {
  return GATEWAY_PROVIDERS.includes(method as GatewayProvider);
}

function paymentMethodForProvider(provider: string): PaymentMethod {
  const p = provider.toUpperCase();
  if (p === 'RAZORPAY' || p === 'STRIPE' || p === 'PAYTM') return p as PaymentMethod;
  return 'UPI';
}

export async function collectGatewayPayment(params: {
  storeId: number;
  invoiceId: number;
  provider: string;
  amount: number;
  currency: string;
  /** UI confirm — pass from useConfirm to avoid window.confirm */
  confirm?: (message: string) => Promise<boolean>;
}): Promise<PaymentRequest> {
  const provider = params.provider.toUpperCase();

  if (provider === 'STRIPE_TERMINAL') {
    const gateways = await posApi.listGateways(params.storeId);
    const terminalGw = gateways.find((g) => g.providerCode === 'STRIPE_TERMINAL');
    return collectStripeTerminalPayment({
      ...params,
      testMode: terminalGw?.testMode,
    });
  }

  const session = await posApi.createCheckoutSession({
    storeId: params.storeId,
    provider,
    amount: params.amount,
    currency: params.currency,
    invoiceId: params.invoiceId,
  });

  if (provider === 'RAZORPAY') {
    await loadRazorpayScript();
    return new Promise((resolve, reject) => {
      openRazorpayCheckout({
        keyId: String(session.keyId ?? ''),
        orderId: String(session.orderId ?? ''),
        amountPaise: Number(session.amount ?? params.amount * 100),
        currency: String(session.currency ?? params.currency),
        planName: `POS Invoice #${params.invoiceId}`,
        onSuccess: (payment) =>
          resolve({
            paymentMethod: 'RAZORPAY',
            amount: params.amount,
            razorpayPaymentId: payment.razorpayPaymentId,
            razorpayOrderId: payment.razorpayOrderId,
            gatewayProvider: 'RAZORPAY',
            gatewayTransactionId: payment.razorpayPaymentId,
            gatewayStatus: 'CAPTURED',
            gatewayReference: payment.razorpayOrderId,
          }),
        onDismiss: () => reject(new Error('Payment cancelled')),
      });
    });
  }

  if (provider === 'STRIPE') {
    return collectStripeOnlinePayment({
      storeId: params.storeId,
      invoiceId: params.invoiceId,
      amount: params.amount,
      currency: params.currency,
      session: session as Record<string, unknown>,
    });
  }

  const label = provider === 'PAYTM' ? 'Paytm' : provider;
  if (params.confirm) {
    const ok = await params.confirm(
      `Confirm ${label} payment of ${params.amount} ${params.currency}?` +
        (session.testMode ? ' (test mode)' : ''),
    );
    if (!ok) throw new Error('Payment cancelled');
  }

  const txnId = String(
    session.paymentIntentId ?? session.txnToken ?? session.orderId ?? `txn_${Date.now()}`,
  );
  return {
    paymentMethod: paymentMethodForProvider(provider),
    amount: params.amount,
    gatewayProvider: provider,
    gatewayTransactionId: txnId,
    gatewayStatus: 'CAPTURED',
    gatewayReference: String(session.orderId ?? txnId),
    transactionRef: txnId,
  };
}

export async function finalizeGatewayInvoice(
  invoiceId: number,
  payment: PaymentRequest,
): Promise<void> {
  await billingApi.addPayment(invoiceId, payment);
}
